import { HeaderImage } from "@surge-global-engineering/css-generator";

import {
  parentBlockTypes,
  fsPatentBlockTypes,
  CurrentChapterConfig,
  shouldRenderFPFormattingForChapter,
  isChapterTitleCardRequired,
} from "../helpers";

import {
  parseBaseParentBlock,
  parseImage,
  parseSocialProfile,
  parseList,
  parseEndnotes,
  parseOrnamentalBreak,
  parseChapterHeader
} from ".";
import { parseTextMessages } from "./textMessages";
import { SlateTextMessages } from "../../../types/slate";
import { TrackChangesProperties } from "../../../components/Plate/plugins/track-changes/types";
import { ATNode } from "../../../components/Plate/config/typescript";


export const parseChapter = (
  chapter: IChapterStore.Chapter,
  config: IThemeStore.ThemeStyleProps,
  isEndnote?: boolean,
  number?: number,
  headerPrefix?: string,
  isExport?: boolean,
  headerImage?: HeaderImage,
  parsedEndnotes?: ISlateStore.SlateEndnote[],
  endnotesOffset?: number,
  endnoteChapter?: IChapterStore.Chapter,
  subheadings?: ISlateStore.SlateSubheading[],
): string => {

  let html = !isEndnote ? `<div class="${chapter.type}">` : "";
  const chapterId = isExport? chapter._id : undefined;

  if(!isEndnote){

    /** render chapter header */
  /** chapter level config to show/hide image */
  const { showChapterImage = true } = chapter?.configuration || {};
  /** chapter level config to show/hide header */
  const { showChapterHeading = true } = chapter?.configuration || {};

  const canRenderInvidualChapterImage =
    config.individualChapterImage && chapter.image ;
  const shouldRenderHeaderImage =
   showChapterImage && 
    Boolean(
      config.titleCard?.image &&
        (config.image?.url || canRenderInvidualChapterImage)
    );

  const imageUrlToRender = headerImage?.url? headerImage?.url: config.individualChapterImage ? chapter.image : config.image?.url;
  const headerElements: IChapterStore.ChapterHeaderElements = {
    title: chapter.title,
    subtitle: chapter.subtitle,
    number,
    image: shouldRenderHeaderImage ? imageUrlToRender: undefined
  };

  const { showDefaultTextColour = true } = chapter?.configuration || {};
  const isLightText = config.image.placement === "background-image" && config.image.headerTextColor === "light";
  const isInverted = showDefaultTextColour ? isLightText : !isLightText;

  if (isChapterTitleCardRequired(chapter.type) && showChapterHeading) {
    html += parseChapterHeader({
      styleProps: config,
      headerElements,
      classPrefix: headerPrefix,
      isInverted
    },
    isExport);
  }}

  const { showFirstSentenceFormatting = true } = chapter.configuration || {};

  const drpCapCls =
    showFirstSentenceFormatting &&
    !isEndnote &&
    shouldRenderFPFormattingForChapter(chapter.type)
      ? "withDropcap"
      : "";
  /** render chapter body */
  !isEndnote?
    html += `<div class="chapter-body ${drpCapCls}">`:"";

  html += !isEndnote ? "<div class=\"wrapper\">": "";

  const chapterConfig = new CurrentChapterConfig();

  const pluginProperties = [
    "underline",
    "bold",
    "italic",
    "monospace",
    "smallcaps",
    "sansserif",
    "subscript",
    "superscript",
    "strikethrough",
    "h2",
    "h3",
    "h4",
    "h5",
    "h6",
  ];
  const formatNodeChildren = (node) => {
    if (node.children) {
      node.children = node.children.map(leaf => {
        if (leaf.trackChanges?.operation === "format") {
          const { trackChanges, ...updatedLeaf } = leaf;
          // Remove the pluginProperties from updatedLeaf
          const cleanedLeaf = Object.keys(updatedLeaf).reduce((acc, key) => {
            if (!pluginProperties.includes(key)) {
              acc[key] = updatedLeaf[key]; // Only include keys not in pluginProperties
            }
            return acc;
          }, {} as Node);
          return {
            ...cleanedLeaf,
            ...trackChanges.formatting, // Apply the original formatting from trackChanges
          };
        }

        if (leaf.children) {
          leaf = formatNodeChildren(leaf);
        }
        
        return leaf;
      });
      node.children = node.children.filter(child => {
        // Keep nodes without trackChanges or where the operation is "delete"
        return !(child.trackChanges) || ((child.trackChanges as TrackChangesProperties).operation === "delete");
      });
    }
    return node;
  };

  const filterTrackChanges = (chapterBody: ATNode) => {
    return chapterBody.map(node => formatNodeChildren(node));
  }; 

  // filter out the track-changes from the chapter body
  const filteredChapterContent = filterTrackChanges(chapter.children);

  for (const [blockIndex, block] of filteredChapterContent.entries()) {
    if (parentBlockTypes.includes(block.type)) {
      html += parseBaseParentBlock(
        block as ISlateStore.SlateBaseParentBlockNode,
        chapterConfig,
        false,
        isEndnote,
        isExport,
        chapterId,
        endnoteChapter,
        endnotesOffset,
        config,
        subheadings
      );
    }

    if (fsPatentBlockTypes.includes(block.type)) {
      html += parseBaseParentBlock(
        block as ISlateStore.SlateBaseParentBlockNode,
        chapterConfig,
        chapterConfig.getFSFormatting(),
        isEndnote,
        isExport,
        chapterId,
        endnoteChapter,
        endnotesOffset,
        config,
        subheadings 
      );
    }

    //apply drop cap in the next paragraph if type is ornamental break
    if (block.type === "ornamental-break" && config.sceneBreakShowSetting !== "no-break") {
      html += parseOrnamentalBreak(config);
      if (config.beginFirstSentence === "break") {
        chapterConfig.setFSFormatting(true);
      }
    }

    // parse image node
    if (block.type === "image") {
      const imageHtml = parseImage(
        block as ISlateStore.SlateImage,
        chapter.children[blockIndex - 2],
        chapter.children[blockIndex - 1],
        chapter.children[blockIndex + 1],
        chapter.children[blockIndex + 2],
        isExport
      );
      html += imageHtml;
    }

    // parse profile links
    if (block.type === "profile") {
      const profileHtml = parseSocialProfile(
        block as ISlateStore.SlateProfileLink
      );
      html += profileHtml;
    }

    
    if (block.type === "text_messages") {
      html += parseTextMessages(block as SlateTextMessages);
    }

    // parse list nodes
    if (block.type === "ul" || block.type === "ol") {
      html += parseList(
        block as ISlateStore.SlateListBlock, 
        chapterConfig, 
        undefined, 
        isExport, 
        endnotesOffset, 
        chapterId, 
        endnoteChapter, 
        config
        );
    }

    // parse page breaks
    if (block.type === "page-break" && isExport) {
      const isNextNodesUndefined =
        chapter.children[blockIndex + 1] === undefined &&
        chapter.children[blockIndex + 2] === undefined;
      const isNextEmptyNodeAndUndefinedNode =
        chapter.children[blockIndex + 1]?.type === "p" &&
        (chapter.children[blockIndex + 1] as any)?.children[0].text === "" &&
        chapter.children[blockIndex + 2] === undefined;
      const lastOnPage =
        isNextNodesUndefined || isNextEmptyNodeAndUndefinedNode;
      if (!lastOnPage) {
        html += "<div style=\"page-break-before: always;\"></div>";
      }
    }
  }

  html += !isEndnote ? "</div>" : "";

  // render endnotes
  if (
    chapterConfig.getEndnotes().length > 0 &&
    config.ePubNotesMode !== "END_OF_BOOK"
  ) {
    html += parseEndnotes(chapterConfig.getEndnotes(), undefined, chapter, config, isExport);
  }

  html += !isEndnote ? "</div>" : "";
  html += !isEndnote ? "</div>" : "";
  

  if (parsedEndnotes) parsedEndnotes.push(...chapterConfig.getEndnotes());
  return html;
};
